import {
  getPortalDocumentUrl,
  useListPortalDepositLetters,
  useListPortalDocuments,
  type PortalDocumentOut,
} from '@myrentalmate/api-client';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { okData, okItems } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { fileSize, formatDate, formatDateTime, formatMoney } from '../../lib/format';
import { NotLinked } from './NotLinked';
import { usePortalHome } from './usePortalHome';

export function PortalDocumentsPage() {
  const { home, notLinked } = usePortalHome();
  const documents = okItems(useListPortalDocuments({ query: { enabled: !!home } }).data);
  const letters = okItems(useListPortalDepositLetters({ query: { enabled: !!home } }).data);
  const [error, setError] = useState<string | null>(null);

  async function download(id: number) {
    setError(null);
    try {
      const link = okData(await getPortalDocumentUrl(id));
      if (link) window.location.assign(link.url);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  const columns: DataColumn<PortalDocumentOut>[] = [
    { label: 'File', render: (d) => d.file_name },
    { label: 'Added', render: (d) => formatDateTime(d.created_at) },
    { label: 'Size', align: 'right', render: (d) => fileSize(d.size_bytes) },
    {
      label: 'Download',
      align: 'right',
      render: (d) => (
        <Button size="small" onClick={() => void download(d.id)}>
          Download
        </Button>
      ),
    },
  ];

  if (notLinked) return <NotLinked />;
  if (!home) return null;
  return (
    <Stack spacing={2}>
      <Typography variant="h4">Documents</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {letters.length > 0 && (
        <DataTable
          rows={letters}
          rowKey={(l) => l.id}
          columns={[
            { label: 'Security deposit letter', render: (l) => l.place },
            { label: 'Date', render: (l) => formatDate(l.decided_on) },
            { label: 'Returned', align: 'right', render: (l) => formatMoney(l.refunded) },
            { label: 'Kept', align: 'right', render: (l) => formatMoney(l.withheld) },
            {
              label: 'Download',
              align: 'right',
              render: (l) => (
                <Button size="small" href={`/api/portal/deposit-letters/${l.id}/pdf`}>
                  Download
                </Button>
              ),
            },
          ]}
        />
      )}
      {documents.length === 0 ? (
        <Typography color="text.secondary">
          Your lease and other files will show here once your property manager shares them.
        </Typography>
      ) : (
        <DataTable columns={columns} rows={documents} rowKey={(d) => d.id} />
      )}
    </Stack>
  );
}
